import { Compass, Heart, Sparkles, Users, Trophy, Quote } from "lucide-react";
import { ASSETS } from "../lib/constants";
import { useContent, resolveImage } from "../contexts/ContentContext";

const PILLAR_ICONS = [Compass, Sparkles, Users];

const badgeClasses = (variant) => {
  if (variant === "navy") return "bg-[#1E3A8A] text-white";
  if (variant === "gold") return "bg-[#FFD100] text-[#0F172A]";
  return "bg-[#2563EB] text-white";
};

export const About = () => {
  const { data: about } = useContent("about");
  if (!about) return null;

  const pillars = about.pillars || [];
  const creds = about.founder_credentials || [];
  const paras = about.founder_paragraphs || [];

  return (
    <section id="about" data-testid="about-section" className="relative py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Foundation Story */}
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 relative lg:sticky lg:top-28">
            <div className="relative space-y-6">
              <div className="relative aspect-square max-w-md mx-auto">
                <div aria-hidden="true" className="absolute inset-3 rounded-[36px] bg-[#FFD100] -translate-x-3 translate-y-3" />
                <div className="relative w-full h-full rounded-[36px] overflow-hidden ring-1 ring-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.16)]">
                  <img src={resolveImage(about.community_image_url) || ASSETS.action.localNews} alt="A community-driven movement" className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} loading="lazy" data-testid="about-image" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#1E3A8A]/30 via-transparent to-[#FFD100]/10" />
                  <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-lg">
                    <span className="w-10 h-10 rounded-full bg-[#2563EB] grid place-items-center shrink-0">
                      <Heart className="w-5 h-5 text-white" fill="currentColor" />
                    </span>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#1D4ED8]">Since 2023</p>
                      <p className="text-sm font-bold text-slate-900 font-display">A community-driven movement.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <img
                  src={resolveImage(about.foundation_logo_url) || ASSETS.foundationLogo}
                  alt="Semaj J. Morgan Foundation"
                  className="w-40 sm:w-48 h-auto"
                  loading="lazy"
                  data-testid="about-foundation-logo"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <span className="section-eyebrow">
              <Heart className="w-3.5 h-3.5" />
              {about.eyebrow}
            </span>
            <h2 data-testid="about-heading" className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
              {about.heading_lead}
              <span className="text-gradient-brand"> {about.heading_accent}</span>.
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">{about.story_paragraph_1}</p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl">{about.story_paragraph_2}</p>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {pillars.map((p, i) => {
                const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
                return (
                  <div key={i} data-testid={`about-pillar-${i}`} className="card-soft p-6">
                    <span className="w-11 h-11 rounded-2xl bg-[#2563EB]/10 grid place-items-center text-[#2563EB]">
                      <Icon className="w-5 h-5" strokeWidth={2.4} />
                    </span>
                    <p className="mt-4 font-display text-lg font-bold text-slate-900">{p.title}</p>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Founder Bio */}
        <div id="founder" data-testid="founder-bio" className="mt-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <span className="section-eyebrow section-eyebrow-gold">
              <Trophy className="w-3.5 h-3.5" />
              {about.founder_eyebrow}
            </span>
            <h3 data-testid="founder-heading" className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
              {about.founder_heading_lead}
              <span className="text-gradient-brand"> {about.founder_heading_accent}</span>
            </h3>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="founder-credentials">
              {creds.map((c, i) => (
                <span key={i} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${badgeClasses(c.variant)}`}>
                  {c.variant === "navy" && <Trophy className="w-3.5 h-3.5" />}
                  {c.text}
                </span>
              ))}
            </div>

            <div className="mt-6 space-y-4 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              {paras.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div data-testid="founder-quote" className="mt-8 relative rounded-3xl bg-slate-50 p-6 md:p-8 border-l-4 border-[#FFD100]">
              <Quote className="absolute -top-3 -left-3 w-8 h-8 text-[#FFD100] bg-white rounded-full p-1.5 shadow" fill="currentColor" />
              <p className="font-display text-lg md:text-xl font-semibold text-slate-900 leading-snug">{about.founder_quote_title}</p>
              <p className="mt-2 text-sm text-slate-600">{about.founder_quote_body}</p>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 relative">
            <div className="relative aspect-[4/5] max-w-sm mx-auto">
              <div aria-hidden="true" className="absolute inset-0 rounded-[36px] bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E3A8A]" />
              <div className="relative w-full h-full rounded-[36px] overflow-hidden">
                <img src={resolveImage(about.founder_image_url) || ASSETS.action.fieldCutout} alt="Semaj Morgan — founder, wide receiver" className="w-full h-full object-contain scale-105" loading="lazy" data-testid="founder-image" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#FFD100] text-[#0F172A] rounded-2xl px-5 py-4 shadow-[0_20px_50px_rgba(255,209,0,0.45)] ring-4 ring-white max-w-[180px]">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5" strokeWidth={2.5} />
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em]">{about.championship_stat_number}</p>
                </div>
                <p className="mt-1.5 font-display text-base font-extrabold leading-tight">{about.championship_stat_label}</p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 max-w-sm mx-auto">
              <div className="rounded-2xl overflow-hidden shadow-[0_12px_30px_rgba(15,23,42,0.14)] ring-1 ring-slate-100 aspect-[3/4] bg-[#0F172A]">
                <img src={resolveImage(about.championship_image_1_url) || ASSETS.additional.michiganRoses} alt="Semaj Morgan, National Championship moment" className="w-full h-full object-cover" style={{ objectPosition: "center 25%" }} loading="lazy" data-testid="founder-championship-photo" />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-[0_12px_30px_rgba(15,23,42,0.14)] ring-1 ring-slate-100 aspect-[3/4] bg-[#0F172A]">
                <img src={resolveImage(about.championship_image_2_url) || ASSETS.action.rings} alt="Michigan Beat Everybody — four championship rings" className="w-full h-full object-cover" style={{ objectPosition: "center center" }} loading="lazy" data-testid="founder-rings" />
              </div>
            </div>
            <p className="mt-3 text-center text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
              {about.championship_caption}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
